import axios from 'axios';
import type * as Rx from 'rxjs';
import { map } from 'rxjs/operators';
import { API_MYEDUZZ } from 'settings';
import type { INewAccount, IOperationList, IRefundBankAccount, IBanklist } from 'v2/interfaces/financial';
import apiService from 'v2/services/api';
import type { ApiService } from 'v2/services/api';

class RefundAccountService {
  private token: string;

  constructor(private apiService: ApiService) {}

  public async validateToken(token: string): Promise<boolean> {
    if (!token) {
      return false;
    }

    const response = await axios({
      baseURL: API_MYEDUZZ,
      url: '/refund-account/validate-token',
      method: 'GET',
      headers: {
        Authorization: token
      }
    });

    if (response.data.data.is_valid) {
      this.setToken(token);
    }

    return response.data.data.is_valid;
  }

  public async uploadFile(file: File | Blob, type: any, filename?: string): Promise<number> {
    const formData = new FormData();
    formData.append('upload_type', type);

    if (filename) {
      formData.append('file', file, filename);
    } else {
      formData.append('file', file);
    }

    const response = await axios({
      baseURL: API_MYEDUZZ,
      url: '/refund-account/fundtransfers/files',
      method: 'POST',
      headers: {
        Authorization: this.getToken()
      },
      data: formData
    });

    return response.data.data.upload_id;
  }

  public setToken(token: string): void {
    this.token = token;
  }

  public getToken(): string {
    return this.token;
  }

  public async getBanks(isTokenValid: boolean, isInternational: boolean): Promise<IBanklist[]> {
    const token = this.getToken();

    if (!isTokenValid || !token) {
      return [];
    }

    const response = await axios({
      baseURL: API_MYEDUZZ,
      url: '/refund-account/fundtransfers/banks',
      params: isInternational ? { international: true } : {},
      method: 'GET',
      headers: {
        Authorization: token
      }
    });

    return Object.keys(response.data.data)
      .map(key => ({ id: key, value: response.data.data[key] }))
      .sort((a, b) => Number(a.id) - Number(b.id));
  }

  public async getOperations(isTokenValid: boolean): Promise<IOperationList[]> {
    const token = this.getToken();

    if (!isTokenValid || !token) {
      return [];
    }

    const response = await axios({
      baseURL: API_MYEDUZZ,
      url: '/refund-account/fundtransfers/operations',
      method: 'GET',
      headers: {
        Authorization: token
      }
    });

    return response.data.data;
  }

  public async createAccount(account: INewAccount) {
    const newAccount = this.formatAccount(account);

    await axios({
      baseURL: API_MYEDUZZ,
      url: '/refund-account',
      method: 'POST',
      data: newAccount,
      headers: {
        Authorization: this.getToken()
      }
    });
  }

  //WITH AUTH

  public getListBanks(): Rx.Observable<IBanklist[]> {
    return this.apiService.get('/fundtransfers/banks').pipe(
      map(response =>
        Object.keys(response.data)
          .map(key => ({ id: key, value: response.data[key] }))
          .sort((a, b) => Number(a.id) - Number(b.id))
      )
    );
  }

  public getListOperations(): Rx.Observable<IOperationList[]> {
    return this.apiService.get('/fundtransfers/operations').pipe(map(response => response.data));
  }

  public getBankAccountRefund(invoiceId: string): Rx.Observable<IRefundBankAccount> {
    return this.apiService.get(`/shopping/${invoiceId}/refund`);
  }

  public saveNewBankAccountForRefund(account: INewAccount) {
    const newAccount = this.formatAccount(account);
    return this.apiService.post('/fundtransfers/addBankAccountRefund', newAccount);
  }

  private formatAccount(account: INewAccount) {
    type NewAccount = Omit<INewAccount, 'imgsIds'>;

    const { imgsIds, ...rest } = account;

    const newAccount: NewAccount = { ...rest };

    const bankCEFId = '104';

    if (newAccount.bank_id !== bankCEFId) {
      newAccount.operation_id = '';
    }

    if (imgsIds['imgDocCNH']) {
      newAccount.driver_license_image = imgsIds['imgDocCNH'];
    }

    if (imgsIds['imgDocCPF']) {
      newAccount.tax_document_image = imgsIds['imgDocCPF'];
    }

    if (imgsIds['imgDocRG']) {
      newAccount.personal_document_image = imgsIds['imgDocRG'];
    }

    if (imgsIds['videoDoc']) {
      newAccount.personal_document_video = imgsIds['videoDoc'];
    }

    if (imgsIds['imgBankReceipt']) {
      newAccount.address_image = imgsIds['imgBankReceipt'];
    }

    return newAccount;
  }
}

const refundAccountService = new RefundAccountService(apiService);

export default refundAccountService;
